<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { debounce } from "lodash-es";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";

import { toast } from "@/common/utils/toast";

import { formGet, formPut } from "@/common/api/rest/form/form";

import type { FormPayloadType } from "@/common/types/form";

import { RegisterTheme } from "@/common/themes";
import { formsForm } from "@/components";
import FormQuestion from "@/components/tabs/form-question.vue";
import { iconMenu, svgIcon } from "@/common/components";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const formPayload = ref<FormPayloadType | null>(null);
const selectedMenu = ref<string>("home");

const isLoading = ref(false);

const accountId = computed(() => String(route.params.id));
const formId = computed(() => String(route.params.form));

const menuTabs = computed(() => [
  {
    key: "home",
    icon: "home-line",
    label: t("common.labels.home"),
  },
  {
    key: "question",
    icon: "field",
    label: t("common.labels.questions"),
  },
]);

function back() {
  router.push(`/${accountId.value}/register/form`);
}

function fetchForm() {
  isLoading.value = true;

  formGet(accountId.value, formId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        formPayload.value = {
          name: data.name,
          code: data.code,
          description: data.description,
          metadata:
            typeof data.metadata === "string"
              ? JSON.parse(data.metadata)
              : typeof data.metadata === "object"
                ? data.metadata
                : null,
        };
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch((error) => {
      console.log("error", error);
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function updateForm(data: FormPayloadType) {
  const payload = {
    ...data,
    metadata:
      typeof data.metadata === "object"
        ? JSON.stringify(data.metadata)
        : data.metadata,
  };

  formPut(accountId.value, formId.value, payload)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchForm();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function changeMenu(key: string) {
  selectedMenu.value = key;
  fetchForm();
}

function updateOrder(data: string[]) {
  if (!formPayload.value) return;

  const metadata = {
    ...formPayload.value.metadata,
    orders: data,
  };

  formPayload.value.metadata = JSON.stringify(metadata);
  updateForm(formPayload.value);
}

const debounceForm = debounce(updateForm, 1000);

onMounted(() => {
  if (formId.value) {
    fetchForm();
  }
});
</script>

<template>
  <register-theme tab-active="form">
    <template #title>
      <div class="flex flex-row justify-between items-center w-full">
        <div class="flex flex-row gap-1 items-center">
          <svg-icon icon="document" size="2rem" />
          {{ t("common.labels.edit", { concat: t("common.labels.form") }) }}
        </div>

        <button type="button" class="btn btn-back btn-sm" @click="back()">
          <svg-icon icon="close" size="1.25rem" />
        </button>
      </div>
    </template>

    <template #content>
      <icon-menu :tabs="menuTabs" :active="selectedMenu" @selected="changeMenu">
        <template #content>
          <template v-if="selectedMenu === 'home'">
            <div class="title">
              {{ t("common.labels.form") }}
            </div>
            <forms-form
              width="100%"
              :data="formPayload"
              @update-data="debounceForm"
            />
          </template>

          <template v-if="selectedMenu === 'question'">
            <template v-if="accountId && formId">
              <form-question
                :account-id="accountId"
                :form-id="formId"
                :ordering="true"
                :orders="formPayload?.metadata?.orders"
                @update:order="updateOrder($event)"
              />
            </template>
          </template>
        </template>
      </icon-menu>
    </template>
  </register-theme>
</template>

<style lang="scss" scoped>
.title {
  display: flex;
  flex-direction: column;
  padding-inline: 0.5rem;
  border-bottom: 1px solid var(--border);
  margin-block: 2rem;
  padding-block-end: 0.5rem;
  font-size: 1.15rem;
  font-weight: bold;
  text-transform: uppercase;
}
</style>
